import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { GlobalSettings } from "../model/settings.model";
import { JwtHelperService } from "@auth0/angular-jwt";
import { UserModel } from "../model/user.model";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";

@Injectable()
export class AuthenticationService {
    
    constructor(
        private http: HttpClient,
        private settings: GlobalSettings,
        private jwtHelper: JwtHelperService) {
    }

    get headers() {
        return new HttpHeaders()
            .set("Content-Type", "application/json")
            .set("env", environment.name)
    }

    set refreshToken(token: string) {
        localStorage.setItem("refresh-token", token)
    }

    set accessToken(token: string) {
        localStorage.setItem('access_token', token)
    }

    getDesignPolicy(): string {
        let decodedToken = this.jwtHelper.decodeToken(this.getToken())

        if(!decodedToken) {
            return null
        }

        return decodedToken["design-policy"]
    }

    getUser(): UserModel {
        let decodedToken = this.jwtHelper.decodeToken(this.getToken())

        if(!decodedToken) {
            return null
        }

        let user = new UserModel()
        user.id = decodedToken.id
        user.name = decodedToken.name
        user.userName = decodedToken.login
        user.email = decodedToken.email
        user.tenantId = decodedToken["tenant-id"]
        user.workspaceId = decodedToken["company-id"]
        user.defaultProcessId = decodedToken["bfx-defaultprocessid"]

        if(Array.isArray(decodedToken.roles)) {
            user.roles = decodedToken.roles
        } else if(decodedToken.roles) {
            user.roles = [ decodedToken.roles ]
        }

        return user
    }

    getToken(): string {
        return this.jwtHelper.tokenGetter()
    }

    getFrontendVersion(): string {
        let decodedToken = this.jwtHelper.decodeToken(this.getToken())

        if(!decodedToken) {
            return "v2"
        }

        if(decodedToken["tenant-id"] == "c5087393-0637-4ee7-be21-e27208c45a68") {
            return "v1"
        } else {
            return "v2"
        }
    }

    hasRole(role: string): boolean {
        let user = this.getUser()

        if(user && user.roles) {
            return user.roles
                .some(r => null != r && r.toLowerCase() == role.toLowerCase())
        }

        return false
    }

    isAuthenticated(): boolean {
        const token = this.getToken();

        let isExpired = true

        try {
            isExpired = this.jwtHelper.isTokenExpired(token)
        } catch (error) {
            this.logout()
        }

        return token && !isExpired;
    }

    login(username: string, password: string, captchaToken?: string): Observable<any> {
        return this.performLogin(
            "Login",
            {
                user: username,
                password: password,
                captchaToken: captchaToken
            });
    }

    loginAssociate(username: string, password: string, associateLogin: string): Observable<any> {
        return this.performLogin(
            "AssociateLogin",
            {
                user: username,
                password: password,
                associateLogin: associateLogin
            });
    }

    private performLogin(path: string, payload: any): Observable<any> {
        return this.http
            .post<any>(`${this.settings.securityApiUrl}/${path}`,
                payload,
                {
                    headers: this.headers
                })
            .pipe(
                map((token) => {
                    if (token.bearerToken) {
                        this.setAuthToken(token.bearerToken);
                        return true;
                    }

                    return false;
                })
            );
    }

    loginGoogle(idToken: string): Observable<any> {
        return this.performLogin(
            "Login/Google",
            {
                idToken: idToken
            }
        );
    }

    logout() {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh-token");
        localStorage.removeItem("dashPageNumber");
        localStorage.removeItem("pageNumber");
        localStorage.removeItem("wid");
        localStorage.removeItem("tid");
    }

    setAuthToken(token: string) {
        localStorage.setItem('access_token',token)
    }

    validateAccess(): Observable<any> {
        return this.http
            .get<any>(`${this.settings.securityApiUrl}/ValidateAccess`,
            {
                headers: new HttpHeaders()
                    .set("Content-Type", "application/json")
                    .set("Authorization", `Bearer ${this.getToken()}`)
            });
    }
}